import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Mail, Phone, User2 } from 'lucide-react';
import { TRANSLATIONS } from '../data/mock';

const PersonalInfoForm = ({ onSubmit, language }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [emailError, setEmailError] = useState('');

  const t = TRANSLATIONS[language];

  // Validation du format email
  const validateEmail = (email) => {
    if (!email) {
      return language === 'fr' ? 'L\'email est requis' : 'Email is required';
    }
    
    // Vérification basique : présence du @ et structure minimale
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email.includes('@')) {
      return language === 'fr' ? 'L\'email doit contenir un @' : 'Email must contain @';
    }
    
    if (!emailRegex.test(email)) {
      return language === 'fr' ? 'Format d\'email invalide (ex: nom@domaine.com)' : 'Invalid email format (ex: name@domain.com)';
    }
    
    return '';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validation en temps réel pour l'email
    if (field === 'email') {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Valider l'email avant soumission
    const emailValidationError = validateEmail(formData.email);
    setEmailError(emailValidationError);
    
    // Vérifier que tous les champs requis sont remplis et que l'email est valide
    if (formData.firstName && formData.lastName && formData.email && !emailValidationError) {
      onSubmit(formData);
    } else if (emailValidationError) {
      // Focus sur le champ email s'il y a une erreur
      document.getElementById('email')?.focus();
    }
  };

  const isValid = formData.firstName && formData.lastName && formData.email && !emailError;

  return (
    <div className="min-h-screen bg-[#0f1c47] flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center text-white relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, #0f1c47 0%, #52a385 50%, #e62c86 100%)'
        }}>
          <CardTitle className="flex items-center justify-center gap-3 text-xl relative z-10">
            <User className="w-6 h-6" />
            {t.personalInfoTitle || 'Vos coordonnées'}
          </CardTitle>
          <p className="text-white/80 text-sm mt-1">
            {t.personalInfoSubtitle || 'Pour recevoir votre analyse personnalisée'}
          </p>
        </CardHeader>
        
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="flex items-center gap-2 text-gray-700 font-medium">
                <User2 className="w-4 h-4 text-[#e62c86]" />
                {t.firstName} *
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder={t.firstName}
                required
                className="w-full border-gray-300 focus:border-[#e62c86] focus:ring-[#e62c86]/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName" className="flex items-center gap-2 text-gray-700 font-medium">
                <User2 className="w-4 h-4 text-[#52a385]" />
                {t.lastName} *
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder={t.lastName}
                required
                className="w-full border-gray-300 focus:border-[#52a385] focus:ring-[#52a385]/20"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-gray-700 font-medium">
                <Mail className="w-4 h-4 text-[#f3a114]" />
                {t.email} *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="exemple@domaine.com"
                required
                className={`w-full ${
                  emailError 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
                    : 'border-gray-300 focus:border-[#f3a114] focus:ring-[#f3a114]/20'
                }`}
              />
              {emailError && (
                <p className="text-red-600 text-sm flex items-center gap-1">
                  <span className="text-red-500">⚠️</span>
                  {emailError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 text-gray-700 font-medium">
                <Phone className="w-4 h-4 text-[#9dbc1d]" />
                {t.phone}
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+33 6 12 34 56 78"
                className="w-full border-gray-300 focus:border-[#9dbc1d] focus:ring-[#9dbc1d]/20"
              />
            </div>

            <Button 
              type="submit"
              disabled={!isValid}
              className="w-full text-white mt-6 py-3 font-semibold shadow-lg transition-all duration-200 disabled:opacity-50 border-0"
              style={{
                background: isValid ? 'linear-gradient(135deg, #52a385 0%, #e62c86 100%)' : 'rgba(107, 114, 128, 0.5)'
              }}
            >
              {t.submit || 'Accéder au diagnostic'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonalInfoForm;